
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthUser } from './useAuthUser';
import { toast } from 'sonner';
import { convertSaoPauloToUTC, convertUTCToSaoPaulo } from '@/utils/timezoneUtils';

/**
 * Interface para mensagem agendada
 * Corresponde à estrutura da tabela scheduled_messages
 */
export interface ScheduledMessage {
  id: string;
  lead_id: string;
  clinica_id: string;
  user_id: string | null;
  lead_nome: string;
  clinica_nome: string;
  mensagem: string;
  data_agendada: string;
  status: 'pendente' | 'enviado' | 'erro' | 'cancelado';
  erro_detalhes: string | null;
  template_oficial_id?: string | null;
  mapeamento_variaveis?: string[] | null;
  created_at: string;
  updated_at: string;
}

/**
 * Interface para criar nova mensagem agendada
 */
export interface CreateScheduledMessage {
  lead_id: string;
  lead_nome: string;
  mensagem: string;
  data_agendada: string; // Horário de São Paulo
  template_oficial_id?: string | null;
  mapeamento_variaveis?: string[];
}

/**
 * Interface para editar mensagem agendada
 */
export interface EditScheduledMessage {
  id: string;
  mensagem: string;
  data_agendada: string; // Horário de São Paulo
}

/**
 * Hook para listar mensagens agendadas da clínica
 */
export const useScheduledMessages = () => {
  const { userProfile } = useAuthUser();

  return useQuery({
    queryKey: ['scheduled-messages', userProfile?.clinica_id],
    queryFn: async () => {
      if (!userProfile?.clinica_id) return [];

      const { data, error } = await supabase
        .from('scheduled_messages')
        .select('*')
        .eq('clinica_id', userProfile.clinica_id)
        .order('data_agendada', { ascending: true });

      if (error) {
        console.error('Erro ao buscar mensagens agendadas:', error);
        throw error;
      }

      return data as ScheduledMessage[];
    },
    enabled: !!userProfile?.clinica_id,
  });
};

/**
 * Hook para buscar mensagens agendadas de um lead específico
 */
export const useLeadScheduledMessages = (leadId: string) => {
  const { userProfile } = useAuthUser();

  return useQuery({
    queryKey: ['scheduled-messages', 'lead', leadId],
    queryFn: async () => {
      if (!leadId || !userProfile?.clinica_id) return [];

      const { data, error } = await supabase
        .from('scheduled_messages')
        .select('*')
        .eq('lead_id', leadId)
        .eq('clinica_id', userProfile.clinica_id)
        .order('data_agendada', { ascending: true });

      if (error) {
        console.error('Erro ao buscar mensagens do lead:', error);
        throw error;
      }

      return data as ScheduledMessage[];
    },
    enabled: !!leadId && !!userProfile?.clinica_id,
  });
};

/**
 * Hook para criar uma nova mensagem agendada
 */
export const useCreateScheduledMessage = (targetClinicaId?: string | null) => {
  const queryClient = useQueryClient();
  const { userProfile, user } = useAuthUser();
  // O modal do chat fornece o tenant do lead; null bloqueia a criação sem seleção.
  const clinicaId = targetClinicaId === undefined ? userProfile?.clinica_id : targetClinicaId;

  return useMutation({
    mutationFn: async (newMessage: CreateScheduledMessage) => {
      if (!clinicaId) {
        throw new Error('Usuário não possui clínica associada');
      }

      // clinicas: SELECT de nome por id da clínica escolhida para identificar o remetente; RLS preservado.
      const { data: clinica, error: clinicaError } = await supabase
        .from('clinicas')
        .select('nome')
        .eq('id', clinicaId)
        .single();

      if (clinicaError) {
        throw new Error('Erro ao buscar dados da clínica');
      }

      // Converter horário de São Paulo para UTC antes de salvar
      const utcDateTime = convertSaoPauloToUTC(newMessage.data_agendada);

      // scheduled_messages: INSERT dos dados do contato, texto/template e horário na clínica escolhida.
      // A agenda só é gravada após confirmação no formulário; o RLS valida a permissão no servidor.
      const { data, error } = await supabase
        .from('scheduled_messages')
        .insert({
          lead_id: newMessage.lead_id,
          clinica_id: clinicaId,
          user_id: user?.id,
          lead_nome: newMessage.lead_nome,
          clinica_nome: clinica.nome,
          mensagem: newMessage.mensagem,
          data_agendada: utcDateTime,
          status: 'pendente',
          template_oficial_id: newMessage.template_oficial_id ?? null,
          mapeamento_variaveis: newMessage.mapeamento_variaveis ?? [],
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar mensagem agendada:', error);
        throw error;
      }

      return data as ScheduledMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages'] });
      toast.success('Mensagem agendada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao agendar mensagem:', error);
      toast.error('Erro ao agendar mensagem');
    },
  });
};

/**
 * Hook para editar uma mensagem agendada
 */
export const useEditScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editData: EditScheduledMessage) => {
      // Converter horário de São Paulo para UTC antes de salvar
      const utcDateTime = convertSaoPauloToUTC(editData.data_agendada);

      const { data, error } = await supabase
        .from('scheduled_messages')
        .update({ 
          mensagem: editData.mensagem,
          data_agendada: utcDateTime,
          updated_at: new Date().toISOString()
        })
        .eq('id', editData.id)
        .eq('status', 'pendente') // Só permite editar se ainda estiver pendente
        .select()
        .single();

      if (error) {
        console.error('Erro ao editar mensagem:', error);
        throw error;
      }

      return data as ScheduledMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages'] });
      toast.success('Mensagem editada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao editar mensagem:', error);
      toast.error('Erro ao editar mensagem');
    },
  });
};

/**
 * Hook para cancelar uma mensagem agendada
 */
export const useCancelScheduledMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageId: string) => {
      const { data, error } = await supabase
        .from('scheduled_messages')
        .update({ 
          status: 'cancelado',
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('status', 'pendente') // Só permite cancelar se ainda estiver pendente
        .select()
        .single();

      if (error) {
        console.error('Erro ao cancelar mensagem:', error);
        throw error;
      }

      return data as ScheduledMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages'] });
      toast.success('Mensagem cancelada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao cancelar mensagem:', error);
      toast.error('Erro ao cancelar mensagem');
    },
  });
};
